import { GoalModel } from '@models/goals';
import { GoalSubscriptionModel, GoalSubscriptionStatus } from '@models/goalSubscriptions';
import { GoalPackageModel } from '@models/orders';
import * as Sentry from '@sentry/nextjs';
import { useCallback, useState } from 'react';
import { useGoalsService } from './useGoalsService';
import { useGoalSubscriptionService } from './useGoalSubscriptionService';
import { useOrdersService } from './useOrdersService';

interface UseGoalSubscriptionAndEnrollableGoalsServiceReturn {
  isLoading: boolean;
  isLoadingSubscriptions: boolean;
  isLoadingEnrollableGoals: boolean;
  isLoadingPackages: boolean;
  error: Error | null;
  getGoalSubscriptionsAndEnrollableGoals: (
    query?: string,
    statuses?: GoalSubscriptionStatus[]
  ) => Promise<GoalSubscriptionAndEnrollableGoalsModel>;
}

interface GoalSubscriptionAndEnrollableGoalsModel {
  goalSubscriptions: GoalSubscriptionModel[];
  enrollableGoals: GoalModel[];
  packages: GoalPackageModel[];
}

export const useGoalSubscriptionAndEnrollableGoalsService =
  (): UseGoalSubscriptionAndEnrollableGoalsServiceReturn => {
    const { getGoalSubscriptions } = useGoalSubscriptionService();
    const { getGoals } = useGoalsService();
    const { getOrders } = useOrdersService();

    const [isLoadingSubscriptions, setIsLoadingSubscriptions] = useState<boolean>(false);
    const [isLoadingEnrollableGoals, setIsLoadingEnrollableGoals] = useState<boolean>(false);
    const [isLoadingPackages, setIsLoadingPackages] = useState<boolean>(false);
    const [isLoadingStatus, setIsLoadingStatus] = useState<boolean>(false);
    const [error, setError] = useState<Error | null>(null);

    const isLoading =
      isLoadingStatus || isLoadingSubscriptions || isLoadingEnrollableGoals || isLoadingPackages;

    const getGoalSubscriptionsAndEnrollableGoals = useCallback(
      async (
        query?: string,
        statuses?: GoalSubscriptionStatus[]
      ): Promise<GoalSubscriptionAndEnrollableGoalsModel> => {
        setError(null);
        setIsLoadingStatus(true);
        setIsLoadingSubscriptions(true);
        setIsLoadingEnrollableGoals(true);
        setIsLoadingPackages(true);
        try {
          const [subscriptionsResult, enrollableGoalsResult, ordersResult] =
            await Promise.allSettled([
              getGoalSubscriptions({
                query: query,
                statuses: statuses,
                excludeChildGoals: true
              }),
              getGoals({
                canEnroll: true,
                excludeSubscribed: true,
                query: query
              }),
              getOrders()
            ]);

          let result: GoalSubscriptionAndEnrollableGoalsModel = {
            goalSubscriptions: [],
            enrollableGoals: [],
            packages: []
          };

          if (subscriptionsResult.status === 'fulfilled') {
            result.goalSubscriptions = subscriptionsResult.value.goalSubscriptions;
          } else {
            Sentry.captureException(subscriptionsResult.reason);
          }

          if (enrollableGoalsResult.status === 'fulfilled') {
            result.enrollableGoals = enrollableGoalsResult.value.goals;
          } else {
            Sentry.captureException(enrollableGoalsResult.reason);
          }

          if (ordersResult.status === 'fulfilled') {
            result.packages = ordersResult.value.packages;
          } else {
            Sentry.captureException(ordersResult.reason);
          }

          return result;
        } catch (err) {
          const error = err instanceof Error ? err : new Error('Unknown error');
          setError(error);
          Sentry.captureException(error);
          return {
            goalSubscriptions: [],
            enrollableGoals: [],
            packages: []
          };
        } finally {
          setIsLoadingSubscriptions(false);
          setIsLoadingEnrollableGoals(false);
          setIsLoadingPackages(false);
          setIsLoadingStatus(false);
        }
      },
      [getGoalSubscriptions, getGoals, getOrders]
    );

    return {
      isLoading,
      isLoadingSubscriptions,
      isLoadingEnrollableGoals,
      isLoadingPackages,
      error,
      getGoalSubscriptionsAndEnrollableGoals
    };
  };

export type { GoalSubscriptionAndEnrollableGoalsModel };
